import { useQuery } from '@apollo/client';
import * as React from 'react';

import * as Nav from 'components/Navigation';
import NavigationTabs, { Tab } from 'components/NavigationTabs';
import Group from 'entities/Group';
import { rootGroupCode } from 'entities/RootGroup';
import UnreadStore from 'stores/UnreadStore';
import CurrentUser from 'auth/CurrentUser';
import { useShouldDisplayConversationsTab } from 'conversations';

import type {
  GroupMembershipRequestsCountQuery,
  GroupMembershipRequestsCountQueryVariables,
} from 'groupMembershipRequests/containers/queries/__generated__/GroupMembershipRequestsCountQuery';
import type {
  GroupNavigationTabsOrganization,
  GroupNavigationTabsOrganizationVariables,
} from 'groups/components/queries/__generated__/GroupNavigationTabsOrganization';
import { GroupNavigationTabsQuery } from 'groups/components/queries/__generated__/GroupNavigationTabsQuery';
import type {
  RoleRequestsCountWithOrgId,
  RoleRequestsCountWithOrgIdVariables,
} from 'roleRequests/containers/queries/__generated__/RoleRequestsCountWithOrgId';
import type {
  PendingChildOrgApprovalRequestsCount,
  PendingChildOrgApprovalRequestsCountVariables,
} from 'groups/components/queries/__generated__/PendingChildOrgApprovalRequestsCount';

import GROUP_MEMBERSHIP_REQUESTS_COUNT_QUERY from 'groupMembershipRequests/containers/queries/GroupMembershipRequestsCountQuery.gql';
import GROUP_NAV_TABS_QUERY from 'groups/components/queries/GroupNavigationTabs.gql';
import GROUP_NAV_TABS_ORGANIZATIONS_QUERY from 'groups/components/queries/GroupNavigationTabsOrganization.gql';
import ROLE_REQUESTS_COUNT_QUERY from 'roleRequests/containers/queries/RoleRequestsCountWithOrgIdQuery.gql';
import PENDING_CHILD_ORG_APPROVAL_REQUESTS_COUNT_QUERY from 'groups/components/queries/PendingChildOrgApprovalRequestsCount.gql';

export type Props = {
  adminTheme?: boolean;
  group: Group;
};

export type PeopleTabContextType = {
  refetch: () => void;
};

// Please note this is an unorthodox way of using context.
// because GroupNavigationTabs are not necessarily the parent component of the things needing to refresh them.
//   Context.Provider will not give context to things that aren't it's children.
export const PeopleTabContextData = {
  refetch: () => {
    /* this is a fake refetch, we override it down below */
  },
};
export const PeopleTabContext = React.createContext<PeopleTabContextType>(PeopleTabContextData);

export function GroupNavigationTabs(props: Props & { hasUnreads: boolean }) {
  const { adminTheme, group, ...others } = props;

  const nav = useQuery<GroupNavigationTabsQuery, {}>(GROUP_NAV_TABS_QUERY, {});

  const orgs = useQuery<GroupNavigationTabsOrganization, GroupNavigationTabsOrganizationVariables>(
    GROUP_NAV_TABS_ORGANIZATIONS_QUERY,
    {
      variables: { organizationId: group.getStaffClassOrgId() },
      skip: !group.isStaffClass(),
    }
  );

  const gateClasses =
    group.isStaffClass() && orgs.data?.organizations?.[0]?.approvalSettings?.gateClasses;

  const hasGroupMembershipRequests = useHasGroupMembershipRequests(props.group);
  const hasRoleRequests = useHasRoleRequests(props.group);
  const hasPendingChildOrgApprovalRequests = useHasPendingChildOrgApprovalRequests(
    props.group,
    !!gateClasses
  );

  const uuid = group.isStaffClass() ? group.getOrganization()?.getUuid() : group.getUuid();

  // these are the u13 conversation unreads
  const navListUnreads = useNavListUnreads(props.group);
  const shouldDisplayConversationsTab = useShouldDisplayConversationsTab(uuid);

  const currentUser = CurrentUser.getUser();
  const user = nav.data?.user;

  const canPhoneCall = !!user?.permissions.canMakePhoneCalls;

  const adminPermissions = orgs.data?.organizations[0]?.permissions.adminConsole;

  const baseUrl = `/classes/${group.getCodeOrError()}`;
  const tabs: Array<Tab> = [];

  if (adminPermissions?.seeHomeTab) {
    tabs.push({
      name: 'Overview',
      path: `${baseUrl}/home`,
    });
  }

  if (group.get('hasMessagesTab')) {
    tabs.push({
      name: 'Messages',
      path: `${baseUrl}/messages`,
      indicator: props.hasUnreads,
    });
  }

  // We ask for both the frontend and backend flags to be enabled because the backend should be controlling
  // the tabs by altering the results of the graphql query that we should be using, this simulates that behavior
  if (
    currentUser.hasFeature('u13.web.feb-2023') &&
    currentUser.hasFeature('u13_conversations.backend.feb-2023') &&
    shouldDisplayConversationsTab
  ) {
    tabs.push({
      name: 'Conversations',
      path: `${baseUrl}/conversations`,
      indicator: !!navListUnreads,
    });
  }

  if (adminPermissions?.seeHomeTab && currentUser.hasFeature('surveys.web.may-22')) {
    tabs.push({
      name: 'Surveys',
      path: `${baseUrl}/surveys`,
    });
  }

  if (showFilesTab(group, user)) {
    tabs.push({
      name: 'Files',
      path: `${baseUrl}/files`,
    });
  }

  if (showPeopleTab(group)) {
    tabs.push({
      name: 'People',
      path: `${baseUrl}/people`,
      indicator: hasRoleRequests || hasGroupMembershipRequests,
    });
  }

  if (canPhoneCall) {
    tabs.push({
      name: 'Calls',
      path: `${baseUrl}/calls`,
    });
  }

  if (adminPermissions?.seeClassesTab) {
    tabs.push({
      name: 'Classes',
      path: `${baseUrl}/classes`,
      indicator: hasPendingChildOrgApprovalRequests,
    });
  }

  if (adminPermissions?.seeSchoolsTab) {
    tabs.push({
      name: 'Schools',
      path: `${baseUrl}/schools`,
    });
  }

  if (adminPermissions?.seeStatisticsTab) {
    tabs.push({
      name: 'Statistics',
      path: `${baseUrl}/statistics`,
    });
  }

  if (adminPermissions?.seeRosteringTab) {
    tabs.push({
      name: 'SIS Sync',
      path: `${baseUrl}/sis_sync`,
    });
  }

  if (adminPermissions?.seeSettingsTab) {
    tabs.push({
      name: 'Settings',
      path: `${baseUrl}/settings/organization`,
    });
  }

  if (!group.isStaffClass() && !group.isRoot()) {
    tabs.push({
      name: group.canEdit() ? 'Settings' : 'Information',
      path: `${baseUrl}/class_settings`,
    });
  }

  if (orgs.loading) {
    return null;
  }

  return (
    <NavigationTabs
      {...others}
      tabs={tabs}
      theme={adminTheme ? 'light' : 'dark'}
      className="GroupNavigationTabs"
    />
  );
}

function useHasRoleRequests(group: Group) {
  const roleRequest = useQuery<RoleRequestsCountWithOrgId, RoleRequestsCountWithOrgIdVariables>(
    ROLE_REQUESTS_COUNT_QUERY,
    {
      variables: {
        organizationId: group.getStaffClassOrgId() as number,
        role: 'teacher',
      },
      fetchPolicy: 'network-only',
      skip: !group.isStaffClass(),
    }
  );
  const roleRequestsCount = roleRequest.data?.organizations[0]?.roleRequestCount ?? 0;
  return roleRequestsCount > 0;
}

function useHasGroupMembershipRequests(group: Group) {
  const groupRequest = useQuery<
    GroupMembershipRequestsCountQuery,
    GroupMembershipRequestsCountQueryVariables
  >(GROUP_MEMBERSHIP_REQUESTS_COUNT_QUERY, {
    variables: {
      groupUuid: group.getUuid(),
    },
    fetchPolicy: 'network-only',
    skip: group.getCode() === rootGroupCode || group.isStaffClass(),
  });

  // Really old hack around context (it might not be working)
  PeopleTabContextData.refetch = groupRequest.refetch;

  const count = groupRequest.data?.classes[0]?.groupMembershipRequestsCount ?? 0;
  return count > 0;
}

function useHasPendingChildOrgApprovalRequests(group: Group, gateClasses: boolean) {
  const childApproval = useQuery<
    PendingChildOrgApprovalRequestsCount,
    PendingChildOrgApprovalRequestsCountVariables
  >(PENDING_CHILD_ORG_APPROVAL_REQUESTS_COUNT_QUERY, {
    variables: {
      organizationId: group.getStaffClassOrgId(),
    },
    fetchPolicy: 'network-only',
    skip: !group.isStaffClass() || !gateClasses,
  });

  const count = childApproval.data?.organizations?.[0]?.pendingChildOrgApprovalRequestsCount ?? 0;
  return count > 0;
}

function useNavListUnreads(group: Group) {
  const uuid = group.isStaffClass() ? group.getOrganization()?.getUuid() : group.getUuid();
  const navItem = Nav.useNavigationItem(uuid ?? '');
  return navItem?.unreadsCount ?? 0;
}

export function showPeopleTab(group: Group): boolean {
  return group.getType() !== 'district' && !group.isRoot();
}

function showFilesTab(group: Group, currentUser: any): boolean {
  if (!group || !currentUser || group.isStaffClass()) {
    return false;
  }

  return currentUser.has_files || currentUser.has_surveys;
}

type State = {
  unreadCount: number;
};

export default class UnreadsProvider extends React.Component<Props, State> {
  state = {
    unreadCount: 0,
  };

  static getStores() {
    return [UnreadStore];
  }

  static calculateState(state: State, props: Props) {
    const { group } = props;
    const groupUuid = group.getUuid();

    if (!groupUuid) {
      return {};
    }

    const isDistrict = group.getType() === 'district';
    const org = group.getOrganization();
    const orgUuid = org ? org.get('uuid') : null;
    const unreadCount =
      isDistrict && orgUuid
        ? UnreadStore.getUnreadCount(groupUuid) + UnreadStore.getUnreadCount(orgUuid)
        : UnreadStore.getUnreadCount(groupUuid);
    return {
      unreadCount,
    };
  }

  hasUnreads = () => {
    return this.state.unreadCount > 0;
  };

  render() {
    return <GroupNavigationTabs hasUnreads={this.hasUnreads()} {...this.props} />;
  }
}
